import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Activity, Shield, ArrowLeft } from 'lucide-react';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // MFA state
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaUserId, setMfaUserId] = useState(null);
  const [mfaMaskedEmail, setMfaMaskedEmail] = useState('');
  const [mfaCode, setMfaCode] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);

  const { login, verifyMfa, resendMfa } = useAuth();
  const navigate = useNavigate();
  const codeInputRefs = useRef([]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-focus first code input when MFA step appears
  useEffect(() => {
    if (mfaStep && codeInputRefs.current[0]) {
      codeInputRefs.current[0].focus();
    }
  }, [mfaStep]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success && result.mfaRequired) {
      setMfaStep(true);
      setMfaUserId(result.userId);
      setMfaMaskedEmail(result.email);
      setResendCooldown(30);
    } else if (result.success) {
      navigate('/diary');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...mfaCode];
    newCode[index] = value;
    setMfaCode(newCode);

    // Auto-advance to next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newCode.every(d => d !== '')) {
      handleVerifyMfa(newCode.join(''));
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !mfaCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setMfaCode(newCode);
      codeInputRefs.current[5]?.focus();
      handleVerifyMfa(pasted);
    }
  };

  const handleVerifyMfa = async (code) => {
    setError('');
    setLoading(true);

    const result = await verifyMfa(mfaUserId, code);

    if (result.success) {
      navigate('/diary');
    } else {
      setError(result.error);
      // Clear code on failure
      setMfaCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    }

    setLoading(false);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setError('');
    const result = await resendMfa(mfaUserId);

    if (result.success) {
      // Update challenge token — resend generates a new random token
      if (result.userId) {
        setMfaUserId(result.userId);
      }
      setResendCooldown(60);
      setMfaCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } else {
      setError(result.error);
    }
  };

  const handleBackToLogin = () => {
    setMfaStep(false);
    setMfaUserId(null);
    setMfaMaskedEmail('');
    setMfaCode(['', '', '', '', '', '']);
    setError('');
    setPassword('');
  };

  const inputFocusStyle = (e) => {
    e.target.style.borderColor = '#2563eb';
    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
  };

  const inputBlurStyle = (e) => {
    e.target.style.borderColor = '#d1d5db';
    e.target.style.boxShadow = 'none';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
        padding: '48px 32px',
        width: '100%',
        maxWidth: '440px'
      }}>
        {/* Logo and Title */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '32px'
        }}>
          <Activity style={{ width: '48px', height: '48px', color: '#2563eb', marginRight: '12px' }} />
          <h1 style={{ fontSize: '36px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
            ClinHx
          </h1>
        </div>

        {!mfaStep ? (
          <>
            {/* ============ PASSWORD STEP ============ */}
            <h2 style={{
              fontSize: '20px',
              color: '#4b5563',
              textAlign: 'center',
              marginBottom: '32px',
              marginTop: 0
            }}>
              Sports Medicine History Taking
            </h2>

            <form onSubmit={handleLogin}>
              {/* Email Field */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  onFocus={inputFocusStyle}
                  onBlur={inputBlurStyle}
                />
              </div>

              {/* Password Field */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  onFocus={inputFocusStyle}
                  onBlur={inputBlurStyle}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#dc2626',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  backgroundColor: loading ? '#9ca3af' : '#2563eb',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s',
                  marginTop: '4px'
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.target.style.backgroundColor = '#1d4ed8';
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.target.style.backgroundColor = '#2563eb';
                }}
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </>
        ) : (
          <>
            {/* ============ MFA CODE STEP ============ */}
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <Shield size={28} color="#2563eb" />
              </div>
              <h2 style={{
                fontSize: '20px',
                color: '#1f2937',
                margin: '0 0 8px',
                fontWeight: '600',
              }}>
                Check your email
              </h2>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: 0,
                lineHeight: '1.5',
              }}>
                We sent a 6-digit code to<br />
                <span style={{ fontWeight: '600', color: '#374151' }}>{mfaMaskedEmail}</span>
              </p>
            </div>

            {/* Code Input */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '24px',
            }}>
              {mfaCode.map((digit, index) => (
                <input
                  key={index}
                  ref={el => codeInputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  onPaste={index === 0 ? handleCodePaste : undefined}
                  disabled={loading}
                  style={{
                    width: '48px',
                    height: '56px',
                    textAlign: 'center',
                    fontSize: '24px',
                    fontWeight: '700',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    color: '#1f2937',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              ))}
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px',
                textAlign: 'center',
              }}>
                {error}
              </div>
            )}

            {/* Loading indicator */}
            {loading && (
              <p style={{
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '14px',
                marginBottom: '20px',
              }}>
                Verifying...
              </p>
            )}

            {/* Resend and Back */}
            <div style={{
              textAlign: 'center',
              marginTop: '8px',
            }}>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 12px' }}>
                Didn't receive the code?{' '}
                {resendCooldown > 0 ? (
                  <span style={{ color: '#9ca3af' }}>
                    Resend in {resendCooldown}s
                  </span>
                ) : (
                  <button
                    onClick={handleResend}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#2563eb',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px',
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                  >
                    Resend code
                  </button>
                )}
              </p>

              <button
                onClick={handleBackToLogin}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 0',
                }}
              >
                <ArrowLeft size={14} /> Back to login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
